/**
 * Folding ranges — collapsible regions in the editor.
 *
 * Emits one folding range per:
 *   - brace block (`{ ... }`)
 *   - block comment (`/* ... *\/`) spanning multiple lines
 *   - run of >= 2 consecutive single-line `//` comments
 *   - region markers: `// #region` ... `// #endregion`
 *
 * Folding is computed from tokens + their trivia, so it survives partial
 * parse errors.
 */

import { FoldingRangeKind, type FoldingRange } from "vscode-languageserver";
import type { Token, Trivia } from "@archlang/engine";
import type { FileState } from "../workspace/workspace.js";

export function provideFoldingRanges(file: FileState): FoldingRange[] {
  const out: FoldingRange[] = [];
  emitBraceRanges(file.tokens, out);
  emitCommentRanges(file.tokens, out);
  emitRegionRanges(file.text, out);
  return out;
}

// =============================================================================
// Brace blocks
// =============================================================================

function emitBraceRanges(tokens: readonly Token[], out: FoldingRange[]): void {
  const stack: number[] = [];
  for (const tok of tokens) {
    if (tok.type === "LBRACE") {
      stack.push(tok.span.start.line - 1);
    } else if (tok.type === "RBRACE") {
      const start = stack.pop();
      if (start === undefined) continue;
      const end = tok.span.start.line - 1;
      if (end > start) out.push({ startLine: start, endLine: end });
    }
  }
}

// =============================================================================
// Comment ranges
// =============================================================================

function emitCommentRanges(tokens: readonly Token[], out: FoldingRange[]): void {
  // Walk every trivia run across all tokens. We collect block comments
  // individually and runs of consecutive single-line comments together.
  const seen = new Set<string>();
  const allTrivia = collectAllTrivia(tokens);

  // Block comments
  for (const tr of allTrivia) {
    if (tr.kind !== "blockComment") continue;
    const startLine = tr.span.start.line - 1;
    const endLine = tr.span.end.line - 1;
    if (endLine > startLine) {
      pushUnique(out, seen, {
        startLine,
        endLine,
        kind: FoldingRangeKind.Comment,
      });
    }
  }

  // Runs of `//` line comments (>= 2 consecutive lines).
  let runStart: number | null = null;
  let runEnd: number | null = null;
  let lastLine = -2;
  for (const tr of allTrivia) {
    if (tr.kind !== "lineComment") continue;
    const line = tr.span.start.line - 1;
    if (line === lastLine + 1 && runStart !== null) {
      runEnd = line;
    } else {
      if (runStart !== null && runEnd !== null && runEnd > runStart) {
        pushUnique(out, seen, {
          startLine: runStart,
          endLine: runEnd,
          kind: FoldingRangeKind.Comment,
        });
      }
      runStart = line;
      runEnd = line;
    }
    lastLine = line;
  }
  if (runStart !== null && runEnd !== null && runEnd > runStart) {
    pushUnique(out, seen, {
      startLine: runStart,
      endLine: runEnd,
      kind: FoldingRangeKind.Comment,
    });
  }
}

function collectAllTrivia(tokens: readonly Token[]): Trivia[] {
  const out: Trivia[] = [];
  for (const t of tokens) {
    for (const tr of t.leadingTrivia) out.push(tr);
    for (const tr of t.trailingTrivia) out.push(tr);
  }
  // Sort by start line, dedup overlap by exact span.
  out.sort((a, b) => a.span.start.offset - b.span.start.offset);
  const seen = new Set<number>();
  return out.filter((tr) => {
    const k = tr.span.start.offset;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

// =============================================================================
// Region markers
// =============================================================================

const REGION_START = /^\s*\/\/\s*#region\b/;
const REGION_END = /^\s*\/\/\s*#endregion\b/;

function emitRegionRanges(text: string, out: FoldingRange[]): void {
  const lines = text.split("\n");
  const stack: number[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    if (REGION_START.test(line)) {
      stack.push(i);
    } else if (REGION_END.test(line)) {
      const start = stack.pop();
      if (start !== undefined && i > start) {
        out.push({
          startLine: start,
          endLine: i,
          kind: FoldingRangeKind.Region,
        });
      }
    }
  }
}

// =============================================================================
// Helpers
// =============================================================================

function pushUnique(
  out: FoldingRange[],
  seen: Set<string>,
  range: FoldingRange
): void {
  const key = `${range.startLine}-${range.endLine}-${range.kind ?? ""}`;
  if (seen.has(key)) return;
  seen.add(key);
  out.push(range);
}
